import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { Center, Loader } from '@mantine/core';
import {
  getSessionExpiresAt,
  getTokenExpiresAt,
  getMsalAccountId,
  isAuthSessionValid
} from 'services/authSession';
import { registerAuthTokenRefresher } from 'services/httpClient';

const MAX_REFRESH_BUFFER_MS = 60 * 1000;
const MIN_REFRESH_BUFFER_MS = 5 * 1000;
const MIN_REFRESH_DELAY_MS = 10 * 1000;

const getRefreshBufferMs = (remainingMs: number) =>
  Math.min(MAX_REFRESH_BUFFER_MS, Math.max(MIN_REFRESH_BUFFER_MS, remainingMs * 0.2));

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { checkAuth, logout, refreshAuthToken } = useAuth();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const verifyAuth = async () => {
      const isAuth = await checkAuth();
      setIsAuthenticated(isAuth);
    };
    verifyAuth();
  }, [checkAuth]);

  useEffect(() => registerAuthTokenRefresher(refreshAuthToken), [refreshAuthToken]);

  useEffect(() => {
    const expiresAt = getSessionExpiresAt();
    if (!expiresAt) return;

    const expireSession = () => {
      logout('expired');
    };

    const verifySession = () => {
      if (!isAuthSessionValid()) {
        expireSession();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        verifySession();
      }
    };

    const timeoutId = window.setTimeout(expireSession, Math.max(0, expiresAt - Date.now()));

    window.addEventListener('focus', verifySession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('focus', verifySession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [logout]);

  useEffect(() => {
    let refreshTimeoutId: number | undefined;
    let disposed = false;

    const scheduleRefresh = () => {
      if (refreshTimeoutId) {
        window.clearTimeout(refreshTimeoutId);
      }

      const tokenExpiresAt = getTokenExpiresAt();
      if (!tokenExpiresAt || !getMsalAccountId() || !isAuthSessionValid()) {
        return;
      }

      const remainingMs = tokenExpiresAt - Date.now();
      const refreshBufferMs = getRefreshBufferMs(Math.max(0, remainingMs));
      const delay = Math.max(MIN_REFRESH_DELAY_MS, remainingMs - refreshBufferMs);

      refreshTimeoutId = window.setTimeout(async () => {
        try {
          await refreshAuthToken();
          if (!disposed) {
            scheduleRefresh();
          }
        } catch {
          if (!disposed) {
            logout('unauthorized');
          }
        }
      }, delay);
    };

    const refreshIfNeeded = () => {
      const tokenExpiresAt = getTokenExpiresAt();
      if (!tokenExpiresAt) {
        return;
      }

      const remainingMs = tokenExpiresAt - Date.now();
      const refreshBufferMs = getRefreshBufferMs(Math.max(0, remainingMs));

      if (remainingMs <= refreshBufferMs) {
        void refreshAuthToken()
          .then(scheduleRefresh)
          .catch(() => logout('unauthorized'));
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfNeeded();
      }
    };

    scheduleRefresh();
    window.addEventListener('focus', refreshIfNeeded);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      if (refreshTimeoutId) {
        window.clearTimeout(refreshTimeoutId);
      }
      window.removeEventListener('focus', refreshIfNeeded);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [logout, refreshAuthToken]);

  if (isAuthenticated === null) {
    return (
      <Center h='100vh'>
        <Loader size='lg' />
      </Center>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

export default AuthGuard;
